import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { setLayoutDisplayPreference, type LayoutDisplayPrefKey } from "@/lib/layoutDisplayPrefs";
import { useWorkspaceChrome } from "@/spa/workspaceChromeContext";
import type { LayoutUser, SidebarProjectSummary } from "@/ui/layoutUser";
import {
  AccountAvatarPicker,
  AccountDeleteSection,
  AccountOauthErrorToast,
  AccountPasskeys,
  AccountWorkspacePreferences,
  ConnectGitHubButton
} from "./client/AccountPageClient";
import { AccountDevices } from "./client/AccountDevices";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export type AccountPageData = {
  pathname: string;
  user: LayoutUser;
  linkedAccounts: { providerId: string }[];
  hasRepoAccess: boolean;
  csrfToken: string;
  sidebarProjects: SidebarProjectSummary[];
};

const providerLabel = (providerId: string, t: (key: string) => string): string => {
  if (providerId.toLowerCase() === "github") return t("common.github");
  return providerId.charAt(0).toUpperCase() + providerId.slice(1);
};

const handleLayoutPref =
  (key: LayoutDisplayPrefKey, value: string) =>
  () => {
    setLayoutDisplayPreference(key, value);
  };

export const AccountPage = ({ data }: { data: AccountPageData }) => {
  const { t } = useTranslation();
  const chrome = useMemo(
    () => ({
      title: t("meta.titleWithApp", { page: t("account.pageTitle"), appName: t("common.appName") }),
      breadcrumbs: [{ label: t("account.pageTitle") }]
    }),
    [t]
  );
  useWorkspaceChrome(chrome);
  return (
    <>
      <AccountOauthErrorToast />
      <div className="mb-8">
        <p className="eyebrow-label mb-2">{t("account.eyebrow")}</p>
        <h1 className="font-serif text-3xl font-semibold tracking-tight md:text-4xl">{t("account.heading")}</h1>
        <p className="mt-2 max-w-xl text-sm text-muted-foreground">{t("account.intro")}</p>
      </div>

      <div className="max-w-[720px] space-y-4">
        <Card id="display" className="scroll-mt-24">
          <CardHeader>
            <CardTitle className="text-base">{t("account.displayCardTitle")}</CardTitle>
            <CardDescription>{t("account.displayCardDesc")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="flex flex-col gap-2">
              <p className="eyebrow-label">{t("account.contentWidth")}</p>
              <div className="grid grid-cols-2 gap-2" role="group" aria-label={t("account.contentWidth")}>
                <button
                  type="button"
                  data-layout-pref="contentWidth"
                  data-value="contained"
                  className="prefs-choice rounded-md border px-3 py-2 text-sm transition-colors"
                  onClick={handleLayoutPref("contentWidth", "contained")}
                >
                  {t("layoutPrefs.focused")}
                </button>
                <button
                  type="button"
                  data-layout-pref="contentWidth"
                  data-value="wide"
                  className="prefs-choice rounded-md border px-3 py-2 text-sm transition-colors"
                  onClick={handleLayoutPref("contentWidth", "wide")}
                >
                  {t("layoutPrefs.wide")}
                </button>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <p className="eyebrow-label">{t("account.density")}</p>
              <div className="grid grid-cols-2 gap-2" role="group" aria-label={t("account.density")}>
                <button
                  type="button"
                  data-layout-pref="density"
                  data-value="comfortable"
                  className="prefs-choice rounded-md border px-3 py-2 text-sm transition-colors"
                  onClick={handleLayoutPref("density", "comfortable")}
                >
                  {t("layoutPrefs.comfortable")}
                </button>
                <button
                  type="button"
                  data-layout-pref="density"
                  data-value="compact"
                  className="prefs-choice rounded-md border px-3 py-2 text-sm transition-colors"
                  onClick={handleLayoutPref("density", "compact")}
                >
                  {t("layoutPrefs.compact")}
                </button>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <p className="eyebrow-label">{t("account.ambientSurface")}</p>
              <div className="grid grid-cols-2 gap-2" role="group" aria-label={t("account.ambientSurface")}>
                <button
                  type="button"
                  data-layout-pref="ambient"
                  data-value="rich"
                  className="prefs-choice rounded-md border px-3 py-2 text-sm transition-colors"
                  onClick={handleLayoutPref("ambient", "rich")}
                >
                  {t("layoutPrefs.alive")}
                </button>
                <button
                  type="button"
                  data-layout-pref="ambient"
                  data-value="muted"
                  className="prefs-choice rounded-md border px-3 py-2 text-sm transition-colors"
                  onClick={handleLayoutPref("ambient", "muted")}
                >
                  {t("layoutPrefs.muted")}
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("account.profile")}</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center gap-4">
            <AccountAvatarPicker image={data.user.image} name={data.user.name} email={data.user.email} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("account.linkedAccounts")}</CardTitle>
          </CardHeader>
          <CardContent>
            {data.linkedAccounts.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("account.noLinkedAccounts")}</p>
            ) : (
              <ul className="space-y-1">
                {data.linkedAccounts.map((acc) => (
                  <li key={acc.providerId} className="text-sm">
                    {providerLabel(acc.providerId, t)}
                  </li>
                ))}
              </ul>
            )}
            {data.linkedAccounts.some((acc) => acc.providerId.toLowerCase() === "github") ? null : (
              <div className="mt-4">
                <ConnectGitHubButton callbackURL={`${window.location.origin}/account`} />
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("account.passkeys")}</CardTitle>
            <CardDescription>{t("account.passkeysDesc")}</CardDescription>
          </CardHeader>
          <CardContent>
            <AccountPasskeys />
          </CardContent>
        </Card>

        <AccountDevices />

        <Card id="workspace-preferences" className="scroll-mt-24">
          <CardHeader>
            <CardTitle className="text-base">{t("account.workspacePreferences")}</CardTitle>
            <CardDescription>{t("account.workspacePrefsDesc")}</CardDescription>
          </CardHeader>
          <CardContent>
            <AccountWorkspacePreferences hasRepoAccess={data.hasRepoAccess} />
          </CardContent>
        </Card>

        <Card className="border-destructive/50">
          <CardHeader>
            <CardTitle className="text-base text-destructive">{t("account.dangerZone")}</CardTitle>
          </CardHeader>
          <CardContent>
            <AccountDeleteSection />
          </CardContent>
        </Card>
      </div>
    </>
  );
};
